import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Lock, LogOut, LayoutDashboard, Users, FileText, CalendarDays, DollarSign,
  BellRing, ShieldCheck, Images, Inbox, Search, ExternalLink,
} from "lucide-react";
import { checkKey, getKey, setKey, clearKey, crm } from "./crmApi";
import { Stat, Loading, Err, fmtMoney } from "./ui";
import { LeadsTab, CustomersTab } from "./tabsPeople";
import { EstimatesTab, JobsTab } from "./tabsWork";
import { PaymentsTab, FollowUpsTab, WarrantiesTab } from "./tabsMoney";
import { PhotosTab } from "./PhotosTab";
import { logo } from "@/utils/constants";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "customers", label: "Customers", icon: Users },
  { id: "estimates", label: "Estimates", icon: FileText },
  { id: "jobs", label: "Jobs", icon: CalendarDays },
  { id: "payments", label: "Payments", icon: DollarSign },
  { id: "followups", label: "Follow-Ups", icon: BellRing },
  { id: "warranties", label: "Warranties", icon: ShieldCheck },
  { id: "photos", label: "Photos", icon: Images },
];

// ---------- Overview ----------
const Overview = ({ goTab }) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    crm("getDashboard").then(setStats).catch(e => { setError(e); setStats({}); });
  }, []);

  const search = async (e) => {
    e.preventDefault();
    if (!term.trim()) return;
    try { setResults(await crm("search", { term: term.trim() })); }
    catch (err) { toast.error(err.message); }
  };

  if (stats === null) return <Loading />;
  return (
    <div className="space-y-8">
      {error && <Err error={error} />}

      {!error && (
        <>
          <form onSubmit={search} className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                data-testid="crm-search-input"
                value={term}
                onChange={e => setTerm(e.target.value)}
                placeholder="Search customers, leads, jobs, estimates..."
                className="w-full bg-ink/80 border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-white placeholder-white/35 focus:border-weld focus:outline-none focus:ring-1 focus:ring-weld transition text-sm"
              />
            </div>
            <button data-testid="crm-search-btn" type="submit" className="px-4 rounded-lg bg-weld/15 border border-weld/30 text-weld-glow text-sm font-semibold hover:bg-weld/25 transition-colors">Search</button>
          </form>

          {results && (
            <div className="rounded-xl border border-white/10 p-4 space-y-3" data-testid="crm-search-results">
              {[
                ["Customers", results.customers, r => `${r["Customer ID"]} — ${r["First Name"]} ${r["Last Name"]} ${r["Phone"]}`, "customers"],
                ["Leads", results.leads, r => `${r["Lead ID"]} — ${r["Requested Service"]} (${r["Lead Status"]})`, "leads"],
                ["Jobs", results.jobs, r => `${r["Job ID"]} — ${r["Job Title"]} (${r["Job Status"]})`, "jobs"],
                ["Estimates", results.estimates, r => `${r["Estimate Number"]} — $${r["Total Estimate"]} (${r["Estimate Status"]})`, "estimates"],
              ].map(([title, list, line, tab]) => (
                <div key={title}>
                  <h4 className="text-weld-glow text-xs uppercase tracking-wider mb-1.5">{title} ({list.length})</h4>
                  {list.slice(0, 5).map((r, i) => (
                    <button key={i} onClick={() => goTab(tab)} className="block w-full text-left text-white/70 text-sm bg-white/5 hover:bg-weld/10 rounded-lg px-3 py-2 mb-1 transition-colors">{line(r)}</button>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div data-testid="crm-stats" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <Stat label="New Leads" value={stats.newLeads} icon={Inbox} accent={stats.newLeads > 0} />
            <Stat label="Leads To Follow Up" value={stats.leadsNeedingFollowUp} icon={BellRing} />
            <Stat label="Estimates Pending" value={stats.estimatesPending} icon={FileText} />
            <Stat label="Estimates Approved" value={stats.estimatesApproved} icon={FileText} />
            <Stat label="Scheduled Jobs" value={stats.scheduledJobs} icon={CalendarDays} />
            <Stat label="Jobs In Progress" value={stats.jobsInProgress} icon={CalendarDays} />
            <Stat label="Completed Jobs" value={stats.completedJobs} icon={CalendarDays} />
            <Stat label="Follow-Ups Due" value={stats.followUpsDue} icon={BellRing} accent={stats.followUpsDue > 0} />
            <Stat label="Outstanding Balances" value={fmtMoney(stats.outstandingBalances)} icon={DollarSign} accent={stats.outstandingBalances > 0} />
            <Stat label="Deposits Received" value={fmtMoney(stats.depositsReceived)} icon={DollarSign} />
            <Stat label="Monthly Revenue" value={fmtMoney(stats.monthlyRevenue)} icon={DollarSign} accent />
            <Stat label="Active Warranties" value={stats.warrantyJobs} icon={ShieldCheck} />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-white/10 p-4">
              <h4 className="text-weld-glow text-xs uppercase tracking-wider mb-3">Recent Customers</h4>
              {(stats.recentCustomers || []).length === 0 && <p className="text-white/30 text-sm">No customers yet — they appear automatically when someone requests a quote.</p>}
              {(stats.recentCustomers || []).map(c => (
                <button key={c["Customer ID"]} onClick={() => goTab("customers")} className="flex items-center justify-between w-full text-left bg-white/5 hover:bg-weld/10 rounded-lg px-3 py-2 mb-1.5 transition-colors">
                  <span className="text-white/80 text-sm">{c["First Name"]} {c["Last Name"]}</span>
                  <span className="text-white/40 text-xs">{c["Customer ID"]}</span>
                </button>
              ))}
            </div>
            <div className="rounded-xl border border-white/10 p-4">
              <h4 className="text-weld-glow text-xs uppercase tracking-wider mb-3">Recent Photo Uploads</h4>
              {(stats.recentUploads || []).length === 0 && <p className="text-white/30 text-sm">No photos filed yet.</p>}
              <div className="flex gap-2 flex-wrap">
                {(stats.recentUploads || []).map(p => (
                  <a key={p["Media ID"]} href={p["Website URL"] || p["Drive URL"]} target="_blank" rel="noopener noreferrer">
                    <img src={p["Website URL"]} alt={p["Project Name"]} className="w-16 h-16 object-cover rounded-lg border border-white/10 hover:border-weld/50 transition-colors" loading="lazy" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Gate ----------
const Gate = ({ onAuth }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setBad(false);
    const ok = await checkKey(code);
    setBusy(false);
    if (ok) { setKey(code); onAuth(); }
    else setBad(true);
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4 blueprint-bg">
      <form data-testid="admin-gate" onSubmit={submit} className="w-full max-w-sm bg-charcoal/80 border border-weld/20 rounded-2xl p-8 backdrop-blur">
        <img src={logo} alt="Deuce's Welding" className="h-12 mx-auto mb-4" />
        <h1 className="font-display text-3xl text-white text-center">Owner CRM</h1>
        <p className="text-white/45 text-sm text-center mt-1 mb-6">Private area — authorized access only.</p>
        <div className="relative mb-4">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
          <input
            data-testid="admin-passcode-input"
            type="password"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Admin passcode"
            autoFocus
            className="w-full bg-ink/80 border border-white/10 rounded-lg pl-9 pr-3 py-3 text-white placeholder-white/35 focus:border-weld focus:outline-none focus:ring-1 focus:ring-weld transition"
          />
        </div>
        {bad && <p data-testid="admin-gate-error" className="text-red-400 text-sm mb-3">Wrong passcode.</p>}
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy}
          className="w-full py-3 rounded-lg bg-weld text-ink font-bold hover:bg-weld-glow transition-colors disabled:opacity-60 shadow-glow"
        >
          {busy ? "Checking..." : "Enter CRM"}
        </button>
        <a href="/" className="block text-center text-white/35 text-xs mt-4 hover:text-weld-glow transition-colors">← Back to website</a>
      </form>
    </div>
  );
};

// ---------- App ----------
const AdminApp = () => {
  const [authed, setAuthed] = useState(!!getKey());
  const [tab, setTab] = useState("overview");

  if (!authed) return <Gate onAuth={() => setAuthed(true)} />;

  const content = {
    overview: <Overview goTab={setTab} />,
    leads: <LeadsTab />,
    customers: <CustomersTab />,
    estimates: <EstimatesTab />,
    jobs: <JobsTab />,
    payments: <PaymentsTab />,
    followups: <FollowUpsTab />,
    warranties: <WarrantiesTab />,
    photos: <PhotosTab />,
  }[tab];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-white">
      <header className="sticky top-0 z-40 bg-ink/90 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={logo} alt="Deuce's Welding" className="h-8" />
            <div className="min-w-0">
              <h1 className="font-display text-xl leading-none truncate">Deuce's CRM</h1>
              <p className="text-white/35 text-[11px]">Lead → Estimate → Job → Payment → Warranty</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="inline-flex items-center gap-1.5 text-white/50 hover:text-weld-glow text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-weld/40 transition-colors">
              <ExternalLink size={13} /> View Site
            </a>
            <button
              data-testid="admin-signout"
              onClick={() => { clearKey(); setAuthed(false); }}
              className="inline-flex items-center gap-1.5 text-white/50 hover:text-red-400 text-xs px-3 py-2 rounded-lg border border-white/10 hover:border-red-500/40 transition-colors"
            >
              <LogOut size={13} /> Sign Out
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-4 pb-2 flex gap-1.5 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t.id ? "bg-weld text-ink" : "bg-white/5 text-white/55 hover:text-weld-glow hover:bg-weld/10"
              }`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6">{content}</main>
    </div>
  );
};

export default AdminApp;
